import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "antd/lib/button";
import Form from "antd/lib/form";
import Input from "antd/lib/input";
import message from "antd/lib/message";
import { getCurrentUser, loginUser } from "../api/auth";
import FooterBar from "../components/helpdesk/FooterBar";
import HeaderBar from "../components/helpdesk/HeaderBar";
import {
  clearAuthSession,
  isAdminUser,
  isUserLoggedIn,
  saveAuthSession,
} from "../utils/auth";
import "../styles/helpdesk.scss";

function LoginPage() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isUserLoggedIn() && isAdminUser()) {
      navigate("/knowledge-base", { replace: true });
      return;
    }
    if (isUserLoggedIn() && !isAdminUser()) {
      clearAuthSession();
    }
  }, [navigate]);

  useEffect(() => {
    if (!location.state?.forbidden) return;
    message.error("Only admin users can access the admin portal.");
  }, [location.state]);

  const handleLogin = async (values) => {
    setSubmitting(true);
    try {
      const loginData = await loginUser({
        email: values.email,
        password: values.password,
      });

      const token = loginData?.token;
      let user = loginData?.user;

      if (!token) {
        throw new Error("Missing token.");
      }

      saveAuthSession({ token, user });
      if (!user) {
        user = await getCurrentUser();
        saveAuthSession({ token, user });
      }

      if (String(user?.role || "").toUpperCase() !== "ADMIN") {
        clearAuthSession();
        message.error("Admin access only.");
        return;
      }

      message.success("Logged in successfully.");
      const targetRoute = location.state?.from || "/knowledge-base";
      navigate(targetRoute, { replace: true });
    } catch {
      clearAuthSession();
      message.error("Invalid email or password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="helpdesk-page">
      <HeaderBar />
      <main className="helpdesk-main">
        <div className="helpdesk-main__inner">
          <section className="auth-login" aria-labelledby="login-title">
            <div className="auth-login__shell">
              <aside
                className="auth-login__intro"
                aria-label="Portal introduction"
              >
                <p className="auth-login__kicker">DIGIONE HELPDESK</p>
                <h1 id="login-title">Admin Portal Access</h1>
                <p className="auth-login__desc">
                  Manage articles, announcements, and team users from one place
                </p>
              </aside>

              <div className="auth-login__panel">
                <h2>Sign In</h2>
                <p>Use your admin credentials to continue.</p>

                <Form
                  form={form}
                  layout="vertical"
                  requiredMark={false}
                  onFinish={handleLogin}
                >
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: "Please enter your email." },
                      {
                        type: "email",
                        message: "Please enter a valid email address.",
                      },
                    ]}
                  >
                    <Input placeholder="admin@example.com" />
                  </Form.Item>

                  <Form.Item
                    label="Password"
                    name="password"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your password.",
                      },
                    ]}
                  >
                    <Input.Password placeholder="Enter password" />
                  </Form.Item>

                  <Button
                    type="primary"
                    htmlType="submit"
                    className="submit-button"
                    loading={submitting}
                  >
                    Login to Dashboard
                  </Button>
                </Form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <FooterBar />
    </div>
  );
}

export default LoginPage;
